import React, { useState } from 'react';
import { ChallengeType } from '../challenge-types';
import { InputMode } from './InputModeToggle';

interface CompactControlBarProps {
    challengeType: ChallengeType;
    onChallengeTypeChange: (type: ChallengeType) => void;
    inputMode: InputMode;
    onInputModeChange: (mode: InputMode) => void;
    onMainAction: () => void;
    mainButtonText: string;
    mainButtonDisabled: boolean;
    isRunning: boolean;
}

const CompactControlBar: React.FC<CompactControlBarProps> = ({
    challengeType,
    onChallengeTypeChange,
    inputMode,
    onInputModeChange,
    onMainAction,
    mainButtonText,
    mainButtonDisabled,
    isRunning
}) => {
    const [showHelp, setShowHelp] = useState(() => {
        // Show help automatically for first-time users
        const hasVisited = localStorage.getItem('scaleflow-visited');
        if (!hasVisited) {
            localStorage.setItem('scaleflow-visited', 'true');
            return true;
        }
        return false;
    });

    return (
        <div className="w-full bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-white/20 p-3 sm:p-4">
            {/* Help tooltip */}
            {showHelp && (
                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                    <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium">Quick Guide</h4>
                        <button
                            onClick={() => setShowHelp(false)}
                            className="text-blue-600 hover:text-blue-800 ml-2"
                        >
                            ✕
                        </button>
                    </div>
                    <div className="space-y-1 text-xs">
                        <p><strong>Forward Mode:</strong> You'll hear "scale degree 3" and identify the note (like E in C major)</p>
                        <p><strong>Inverse Mode:</strong> You'll hear a note and identify which scale degree it is</p>
                        <p><strong>Manual Input:</strong> Click buttons to select your answer</p>
                        <p><strong>Audio Input:</strong> Speak your answer (requires microphone)</p>
                        <p className="text-blue-600 font-medium mt-2">Try Forward + Manual mode to start!</p>
                    </div>
                </div>
            )}

            {/* Mobile-first layout: stacked on small screens, side-by-side on larger */}
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                {/* Toggle Controls - Horizontal on mobile */}
                <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                    {/* Mode labels - only show on mobile */}
                    <div className="flex justify-between w-full text-xs text-slate-500 font-medium sm:hidden">
                        <span>Mode</span>
                        <span>Input</span>
                    </div>

                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        {/* Challenge Mode Toggle - Compact */}
                        <div className="flex bg-slate-100 rounded-lg p-0.5 flex-1 sm:flex-none">
                            <button
                                onClick={() => onChallengeTypeChange(ChallengeType.FORWARD)}
                                disabled={isRunning}
                                className={`
                                flex-1 sm:flex-none px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200
                                ${challengeType === ChallengeType.FORWARD
                                        ? 'bg-indigo-600 text-white shadow-sm'
                                        : 'text-slate-600 hover:text-slate-800 hover:bg-white hover:shadow-sm'
                                    }
                                ${isRunning ? 'opacity-50 cursor-not-allowed' : ''}
                            `}
                            >
                                <span className="sm:hidden">Forward</span>
                                <span className="hidden sm:inline">🎵 Forward</span>
                            </button>
                            <button
                                onClick={() => onChallengeTypeChange(ChallengeType.INVERSE)}
                                disabled={isRunning}
                                className={`
                                flex-1 sm:flex-none px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200
                                ${challengeType === ChallengeType.INVERSE
                                        ? 'bg-indigo-600 text-white shadow-sm'
                                        : 'text-slate-600 hover:text-slate-800 hover:bg-white hover:shadow-sm'
                                    }
                                ${isRunning ? 'opacity-50 cursor-not-allowed' : ''}
                            `}
                            >
                                <span className="sm:hidden">Inverse</span>
                                <span className="hidden sm:inline">🔄 Inverse</span>
                            </button>
                        </div>

                        {/* Input Mode Toggle - Compact */}
                        <div className="flex bg-slate-100 rounded-lg p-0.5 flex-1 sm:flex-none">
                            <button
                                onClick={() => onInputModeChange('manual')}
                                disabled={isRunning}
                                className={`
                                flex-1 sm:flex-none px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200
                                ${inputMode === 'manual'
                                        ? 'bg-indigo-600 text-white shadow-sm'
                                        : 'text-slate-600 hover:text-slate-800 hover:bg-white hover:shadow-sm'
                                    }
                                ${isRunning ? 'opacity-50 cursor-not-allowed' : ''}
                            `}
                            >
                                <span className="sm:hidden">Manual</span>
                                <span className="hidden sm:inline">🎹 Manual</span>
                            </button>
                            <button
                                onClick={() => onInputModeChange('audio')}
                                disabled={isRunning}
                                className={`
                                flex-1 sm:flex-none px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200
                                ${inputMode === 'audio'
                                        ? 'bg-indigo-600 text-white shadow-sm'
                                        : 'text-slate-600 hover:text-slate-800 hover:bg-white hover:shadow-sm'
                                    }
                                ${isRunning ? 'opacity-50 cursor-not-allowed' : ''}
                            `}
                            >
                                <span className="sm:hidden">Audio</span>
                                <span className="hidden sm:inline">🎤 Audio</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Action Button and Help - Full width on mobile, auto on desktop */}
                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <button
                        data-testid="main-control-btn"
                        onClick={onMainAction}
                        disabled={mainButtonDisabled}
                        className={`
                            flex-1 sm:flex-none px-6 sm:px-8 py-3 rounded-lg font-semibold text-base sm:text-lg 
                            transition-all duration-200 shadow-lg hover:shadow-xl active:scale-95
                            min-h-[48px] touch-manipulation
                            ${isRunning
                                ? 'bg-red-600 hover:bg-red-700 text-white'
                                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                            }
                            ${mainButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                    >
                        {mainButtonText}
                    </button>

                    {/* Help button */}
                    <button
                        onClick={() => setShowHelp(!showHelp)}
                        className="p-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-800 
                                   transition-all duration-200 min-h-[48px] min-w-[48px] flex items-center justify-center"
                        title="Show help"
                    >
                        ?
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CompactControlBar;